import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, AlertTriangle, Target, FileCode, FlaskConical, Play, Wand2,
  Send, Check, X, Save, Archive, Plus,
} from 'lucide-react';
import { Button } from '../Button';
import { regoVerdictView, REGO_TONE_COLORS, regoConflictHint } from '../../../utils/regoVerdict';
import { useAuth } from '../../../contexts/AuthContext';
import { IntentMatcherEditor } from './IntentMatcherEditor';
import { IntentLayersExplainer } from './IntentLayersExplainer';
import {
  listIntentTemplateVersions,
  editIntentTemplateDraft,
  dryRunIntentRego,
  submitIntentTemplateVersion,
  approveIntentTemplateVersion,
  rejectIntentTemplateVersion,
  getWorkflows,
  archiveIntentTemplate,
  createIntentTemplateVersion,
  getAgents,
  getAgentSkills,
  getUsers,
} from '../../../services/api';
import { DecisionPolicyWizard, ROLES as APPROVER_ROLES } from './DecisionPolicyWizard';
import { EntityPicker } from '../EntityPicker';

/**
 * Intent Authorization Console — the consolidated left-nav authoring surface
 * (per the approved mockup). For the selected template's working version it
 * edits ALL THREE layers inline on ONE surface (no slideout):
 *   1. Allowed skill matchers (catalog-aware inline editor)
 *   2. Intent Rego policy (fine gate) — authored inline
 *   3. Dry-run simulation
 * plus autonomy / approval, the lifecycle actions (Save / Submit / Approve /
 * Reject), and the Template → Bind → Enforce → Seal explainer.
 *
 * DRAFT versions are fully editable; IN_REVIEW/APPROVED/DEPRECATED render
 * read-only (immutable). One "Save draft" persists matchers + rego + autonomy +
 * approval together. Per-agent BIND + MATERIALIZE happens on the agent's Intent
 * journey step.
 */
export function IntentAuthoringConsole({ template, onMutate, onArchived }) {
  const { hasPermission, session } = useAuth();
  const canAuthor = hasPermission('compliance.admin') || hasPermission('policy.admin');
  // PROVE7-056 — active governance scope. Every scoped fetch below is
  // bound to the org/tenant/instance the author is operating in, exactly
  // as the workflow canvas binds its own list. This keeps the route-
  // workflow picker to targets valid in THIS scope instead of the full
  // platform set a broad (SA/PA) query would return.
  const orgId = session?.orgId;
  const tenantId = session?.tenantId;
  const instanceId = session?.instanceId;

  const [versions, setVersions] = useState([]);
  const [state, setState] = useState('loading'); // loading | ready | error
  const [errorMsg, setErrorMsg] = useState(null);
  const [workingId, setWorkingId] = useState(null);

  // Editable draft state (mirrors the working DRAFT version; one Save persists all).
  const [draft, setDraft] = useState({ matchers: [], rego: '', autonomy: '', requiresApproval: false });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // { kind, text }
  // PROVE7-045 — Decision Policy Wizard tab + workflow list for the route-workflow picker.
  const [policyTab, setPolicyTab] = useState('wizard');
  const [workflows, setWorkflows] = useState([]);
  // PROVE7-052 — optional agent-first authoring: preview the Wizard against a
  // specific agent's concrete skills + capability palette (nothing is bound).
  const [agents, setAgents] = useState([]);
  const [previewAgentId, setPreviewAgentId] = useState('');
  const [agentSkills, setAgentSkills] = useState([]);
  const [users, setUsers] = useState([]); // PROVE7-054 — optional specific-approver picker
  const [usersByRole, setUsersByRole] = useState({}); // PROVE7-054 — role→holders map; picker offers only role holders
  // PROVE7-048 — reversible template archive (compliance.admin/policy.admin; backend blocks if active bindings exist).
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const load = useCallback(async () => {
    if (!template?.id) return;
    setState('loading'); setErrorMsg(null);
    const res = await listIntentTemplateVersions(template.id);
    if (res?.ok === false) { setState('error'); setErrorMsg(res.error || 'Failed to load versions'); return; }
    const arr = Array.isArray(res?.data ?? res) ? (res?.data ?? res) : [];
    setVersions(arr);
    const byNum = [...arr].sort((a, b) => (b.versionNumber || 0) - (a.versionNumber || 0));
    const pick = byNum.find((v) => v.state === 'DRAFT')
      || byNum.find((v) => v.state === 'IN_REVIEW')
      || byNum.find((v) => v.id === template.activeVersionId)
      || byNum.find((v) => v.state === 'APPROVED')
      || byNum[0];
    setWorkingId(pick ? pick.id : null);
    setState('ready');
  }, [template?.id, template?.activeVersionId]);

  useEffect(() => { load(); }, [load]);

  const working = useMemo(() => versions.find((v) => v.id === workingId) || null, [versions, workingId]);
  const editable = !!working && working.state === 'DRAFT' && canAuthor;
  // PROVE7-050 — an open draft/in-review blocks creating another version.
  const hasOpenVersion = useMemo(
    () => versions.some((v) => v.state === 'DRAFT' || v.state === 'IN_REVIEW'),
    [versions],
  );

  // Reset the editable draft whenever the working version changes.
  useEffect(() => {
    if (!working) return;
    setDraft({
      matchers: Array.isArray(working.allowedSkillMatchers) ? working.allowedSkillMatchers : [],
      rego: working.intentRego || '',
      autonomy: working.maxAutonomy || '',
      requiresApproval: !!working.requiresApproval,
    });
    setPolicyTab(!working.intentRego || working.intentRego.includes('@prove7-wizard-model') ? 'wizard' : 'rego');
    setDirty(false); setMsg(null);
  }, [workingId]); // eslint-disable-line

  const patch = (p) => { setDraft((d) => ({ ...d, ...p })); setDirty(true); setMsg(null); };

  // PROVE7-045 — per-template skill vocabulary for the Wizard: this draft's skill-name matchers.
  const skillNames = useMemo(() => Array.from(new Set((draft.matchers || [])
    .filter((mm) => mm && mm.mode === 'skill-name' && (mm.value || '').trim() !== '')
    .map((mm) => mm.value.trim()))), [draft.matchers]);

  // PROVE7-052 — load the agent list once for the "Preview against agent" picker.
  useEffect(() => {
    let active = true;
    getAgents({}).then((res) => {
      if (!active) return;
      const arr = Array.isArray(res) ? res : (res && (res.data || res.agents)) || [];
      setAgents(arr.filter((a) => a && a.id).map((a) => ({
        id: a.id,
        name: a.name || a.displayName || a.id,
        group: (a.isExternal || a.agentClassification === 'EXTERNAL' || a.type === 'external' || a.archetype === 'ExteLet') ? 'ExteLet'
          : (a.isSystemAgent || a.system || a.kind === 'system' || a.archetype === 'SysLet') ? 'System'
          : (a.autoGenerated || a.archetype === 'AutoLet') ? 'AutoLet'
          : 'Native',
      })).sort((x, y) => (x.name || '').localeCompare(y.name || '')));
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  // PROVE7-054 — tenant users for the Wizard's optional "specific approver" picker.
  useEffect(() => {
    let active = true;
    getUsers({}).then((res) => {
      if (!active) return;
      const arr = Array.isArray(res) ? res : (res && (res.data || res.users)) || [];
      setUsers(arr.filter((u) => u && u.id).map((u) => ({ id: u.id, name: u.name || u.displayName || u.email || u.id })).sort((x, y) => (x.name || '').localeCompare(y.name || '')));
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  // PROVE7-054 — role→holders map for the wizard's "specific approver" picker. Fetching per role
  // (server-side membership filter) is the single source of truth, so the picker can only ever
  // offer users who actually HOLD the selected approver role — you can no longer pick someone the
  // risk workflow would reject at run time. Bounded to the fixed approver-role set.
  useEffect(() => {
    let active = true;
    const mapUsers = (res) => {
      const arr = Array.isArray(res) ? res : (res && (res.data || res.users)) || [];
      return arr.filter((u) => u && u.id)
        .map((u) => ({ id: u.id, name: u.name || u.displayName || u.email || u.id }))
        .sort((x, y) => (x.name || '').localeCompare(y.name || ''));
    };
    Promise.all(APPROVER_ROLES.map((r) =>
      getUsers({ role: r }).then((res) => [r, mapUsers(res)]).catch(() => [r, []])
    )).then((pairs) => {
      if (!active) return;
      setUsersByRole(Object.fromEntries(pairs));
    });
    return () => { active = false; };
  }, []);

  // PROVE7-052 — load the selected agent's skills (with capability tags).
  useEffect(() => {
    if (!previewAgentId) { setAgentSkills([]); return; }
    let active = true;
    getAgentSkills(previewAgentId).then((res) => {
      if (!active) return;
      const arr = Array.isArray(res) ? res : (res && res.data) || [];
      setAgentSkills(Array.isArray(arr) ? arr : []);
    }).catch(() => { if (active) setAgentSkills([]); });
    return () => { active = false; };
  }, [previewAgentId]);

  const agentSkillNames = useMemo(
    () => Array.from(new Set((agentSkills || []).map((sk) => sk && sk.name).filter(Boolean))),
    [agentSkills],
  );
  const agentCapabilities = useMemo(() => {
    const set = new Set();
    (agentSkills || []).forEach((sk) => {
      const ct = sk && sk.capabilityTags;
      if (Array.isArray(ct)) ct.forEach((t) => { if (t) set.add(t); });
    });
    return Array.from(set);
  }, [agentSkills]);
  // Agent skills primary when an agent is chosen; matcher names otherwise. The
  // Wizard input keeps free-text either way (datalist, not a locked select).
  const effectiveSkillNames = previewAgentId ? agentSkillNames : skillNames;
  // PROVE7-055 — agent picker category tabs, only for groups actually present.
  const agentGroups = useMemo(() => {
    const order = [['ExteLet', 'ExteLets'], ['Native', 'Native'], ['AutoLet', 'AutoLets'], ['System', 'System']];
    const present = new Set((agents || []).map((a) => a.group));
    return order.filter(([id]) => present.has(id)).map(([id, label]) => ({ id, label }));
  }, [agents]);

  // PROVE7-045 — load tenant workflows once for the route-workflow picker.
  useEffect(() => {
    let active = true;
    // Scope the picker to the active org/tenant/instance so the author
    // only sees route targets authorised in this scope — never the full
    // platform list. standaloneOnly — agent-bound CEGs aren't valid targets.
    getWorkflows({ orgId, tenantId, instanceId, standaloneOnly: true }).then((res) => {
      if (!active) return;
      const arr = Array.isArray(res) ? res : (res && (res.workflows || res.data)) || [];
      setWorkflows(arr.filter((w) => w && (w.status === 'active' || w.status === 'draft'))
        .map((w) => ({ id: w.id, name: w.name }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    }).catch(() => {});
    return () => { active = false; };
  }, [template?.id, orgId, tenantId, instanceId]);

  const saveDraft = async () => {
    if (!working) return;
    setSaving(true); setMsg(null);
    const cleaned = (draft.matchers || [])
      .map((m) => ({ mode: m.mode, value: (m.value || '').trim() }))
      .filter((m) => m.value !== '');
    const res = await editIntentTemplateDraft(working.id, {
      skillMatchers: cleaned, connectors: null,
      maxAutonomy: draft.autonomy.trim() === '' ? null : draft.autonomy,
      requiresApproval: draft.requiresApproval,
      intentRego: draft.rego,
    }, template.etag);
    setSaving(false);
    if (res?.status === 412 || res?.etagMismatch) { setMsg({ kind: 'warn', text: 'Template changed elsewhere — Reload and re-apply.' }); return; }
    if (res?.ok === false) { setMsg({ kind: 'error', text: res.error || 'Save failed' }); return; }
    setMsg({ kind: 'ok', text: 'Saved.' });
    setDirty(false);
    await load(); if (onMutate) onMutate();
  };

  const lifecycle = async (fn) => {
    setSaving(true); setMsg(null);
    const res = await fn();
    setSaving(false);
    if (res?.ok === false) { setMsg({ kind: 'error', text: res.error || 'Action failed' }); return false; }
    await load(); if (onMutate) onMutate();
    return true;
  };

  // PROVE7-048 — archive the whole template (all versions). Reversible (state -> ARCHIVED).
  const doArchive = async () => {
    setArchiving(true); setMsg(null);
    const res = await archiveIntentTemplate(template.id, template.etag);
    setArchiving(false);
    if (res?.status === 412 || res?.etagMismatch) { setMsg({ kind: 'warn', text: 'Template changed elsewhere — Reload and try Archive again.' }); return; }
    if (res?.status === 409) { setMsg({ kind: 'warn', text: 'Agents are still bound to this template — detach those bindings first, then archive.' }); return; }
    if (res?.ok === false) { setMsg({ kind: 'error', text: res.error || 'Archive failed' }); return; }
    setConfirmArchive(false);
    if (onArchived) onArchived(); else { await load(); if (onMutate) onMutate(); }
  };

  // PROVE7-050 — revise an APPROVED/DEPRECATED template: clone the latest
  // version into a fresh DRAFT (v(N+1)) and land the author on it.
  const doNewVersion = async () => {
    setSaving(true); setMsg(null);
    const res = await createIntentTemplateVersion(template.id, template.etag);
    setSaving(false);
    if (res?.status === 412 || res?.etagMismatch) { setMsg({ kind: 'warn', text: 'Template changed elsewhere — Reload and try again.' }); return; }
    if (res?.status === 409) { setMsg({ kind: 'warn', text: res.error || 'A draft version already exists — edit that one.' }); return; }
    if (res?.ok === false) { setMsg({ kind: 'error', text: res.error || 'Could not create a new version' }); return; }
    const nv = res?.data ?? res;
    await load();
    if (nv?.id) setWorkingId(nv.id);
    if (onMutate) onMutate();
    setMsg({ kind: 'ok', text: `Created v${nv?.versionNumber ?? ''} draft — edit and submit for review.` });
  };

  if (!template) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* header + lifecycle */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Target size={18} color="var(--v2-accent-cyan)" />
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--v2-text-primary)' }}>{template.displayName || template.name}</div>
            <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', fontFamily: 'ui-monospace, monospace' }}>{template.name}</div>
          </div>
          {working && <VersionBadge version={working} />}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <Lifecycle working={working} editable={editable} canAuthor={canAuthor} dirty={dirty} saving={saving}
            onSave={saveDraft}
            onSubmit={() => lifecycle(() => submitIntentTemplateVersion(working.id))}
            onApprove={() => lifecycle(() => approveIntentTemplateVersion(working.id))}
            onReject={(reason) => lifecycle(() => rejectIntentTemplateVersion(working.id, reason))} />
          {msg && <span style={{ fontSize: 12, color: msg.kind === 'ok' ? 'var(--v2-success)' : (msg.kind === 'warn' ? 'var(--v2-warning)' : 'var(--v2-danger)') }}>{msg.text}</span>}
          {canAuthor && template.state !== 'ARCHIVED' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {!confirmArchive ? (
                <Button variant="secondary" size="sm" iconLeft={<Archive size={13} />} disabled={archiving}
                  onClick={() => { setMsg(null); setConfirmArchive(true); }}
                  title="Archive this template (reversible). Blocked while agents are still bound to it.">
                  Archive template
                </Button>
              ) : (
                <>
                  <span style={{ fontSize: 12, color: 'var(--v2-text-secondary)' }}>Archive this template?</span>
                  <Button variant="danger" size="sm" loading={archiving} onClick={doArchive}>Archive</Button>
                  <Button variant="secondary" size="sm" disabled={archiving} onClick={() => setConfirmArchive(false)}>Cancel</Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {state === 'loading' && <Inline><Loader2 size={14} className="animate-spin" /> Loading versions…</Inline>}
      {state === 'error' && <Banner kind="error">{errorMsg}</Banner>}
      {state === 'ready' && !working && <Banner kind="warn">This template has no versions yet.</Banner>}

      {state === 'ready' && working && (
        <>
          {!editable && working.state !== 'DRAFT' && (
            <Banner kind="warn">
              v{working.versionNumber} is {working.state} — immutable.{canAuthor && !hasOpenVersion ? ' Create a new version to revise.' : (hasOpenVersion ? ' A newer draft is already in progress.' : '')}
            </Banner>
          )}
          {!editable && working.state !== 'DRAFT' && canAuthor && !hasOpenVersion && (
            <div style={{ marginTop: 2, marginBottom: 4 }}>
              <Button variant="secondary" size="sm" iconLeft={<Plus size={13} />} loading={saving} onClick={doNewVersion}>
                New version
              </Button>
            </div>
          )}

          {/* regions 1 + 2 side by side */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.2fr)', gap: 16 }}>
            {/* 1 · matchers */}
            <div>
              <SectionLabel>1 · Allowed skill matchers <Faint>— coarse allowlist</Faint></SectionLabel>
              {editable ? (
                <IntentMatcherEditor value={draft.matchers} onChange={(m) => patch({ matchers: m })} disabled={saving} />
              ) : (
                <MatchersRead matchers={working.allowedSkillMatchers} />
              )}
              <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', marginTop: 8 }}>
                Resolved to concrete skill IDs at bind time (the runtime fast-gate).
              </div>

              {/* autonomy + approval (editable inline) */}
              <div style={{ marginTop: 16 }}>
                <SectionLabel>Autonomy ceiling <Faint>— optional</Faint></SectionLabel>
                <select value={draft.autonomy} disabled={!editable} onChange={(e) => patch({ autonomy: e.target.value })} style={{ ...fieldStyle, width: '100%' }}>
                  <option value="">(no ceiling)</option>
                  <option value="HUMAN_CONTROLLED">HUMAN_CONTROLLED</option>
                  <option value="ASSISTED">ASSISTED</option>
                  <option value="SUPERVISED">SUPERVISED</option>
                  <option value="AUTONOMOUS">AUTONOMOUS</option>
                </select>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--v2-text-primary)', marginTop: 10, cursor: editable ? 'pointer' : 'default' }}>
                  <input type="checkbox" checked={draft.requiresApproval} disabled={!editable} onChange={(e) => patch({ requiresApproval: e.target.checked })} />
                  <span>Calls under this intent require explicit human approval.</span>
                </label>
              </div>
            </div>

            {/* 2 · rego */}
            <div>
              <SectionLabel>2 · Intent Rego policy <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--v2-accent-cyan)' }}>— fine gate</span></SectionLabel>
              {editable ? (
                <>
                  {/* PROVE7-052 — optional agent-first preview: source the Wizard's skill
                      vocabulary + capability palette from a specific agent. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--v2-text-muted)' }}>Preview against agent</label>
                    <div style={{ minWidth: 280 }}>
                      <EntityPicker
                        items={agents}
                        value={previewAgentId || null}
                        onChange={(id) => setPreviewAgentId(id || '')}
                        getId={(a) => a.id}
                        getLabel={(a) => a.name}
                        getBadge={(a) => a.group || null}
                        groups={agentGroups}
                        getGroupId={(a) => a.group}
                        allowClear
                        clearLabel="Template matchers (default)"
                        clearSublabel="Use this template's own skill-name matchers"
                        ariaLabel="Preview against agent"
                        placeholder="Template matchers (default)"
                        searchPlaceholder="Search agents"
                      />
                    </div>
                    {previewAgentId && (
                      <span style={{ fontSize: 11, color: 'var(--v2-text-muted)' }}>
                        {agentSkillNames.length} skill{agentSkillNames.length === 1 ? '' : 's'} available
                      </span>
                    )}
                  </div>
                  {previewAgentId && agentCapabilities.length > 0 && (
                    <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', marginBottom: 8 }}>
                      Capabilities this agent's skills carry:{' '}
                      {agentCapabilities.map((t) => (
                        <span key={t} style={{ display: 'inline-block', margin: '0 4px 4px 0', padding: '1px 8px', borderRadius: 999, border: '1px solid var(--v2-border-default)', background: 'var(--v2-bg-surface)', color: 'var(--v2-text-secondary)' }}>{t}</span>
                      ))}
                    </div>
                  )}
                  {/* PROVE7-045 — Wizard | Rego tabs (build the fine gate no-code, or drop to raw Rego) */}
                  <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                    {[['wizard', 'Wizard'], ['rego', 'Rego']].map(([k, l]) => (
                      <button key={k} type="button" onClick={() => setPolicyTab(k)}
                        style={{
                          fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 6,
                          border: '1px solid var(--v2-border-default)', cursor: 'pointer',
                          background: policyTab === k ? 'var(--v2-bg-surface)' : 'transparent',
                          color: policyTab === k ? 'var(--v2-text-primary)' : 'var(--v2-text-muted)',
                        }}>{l}</button>
                    ))}
                  </div>
                  {policyTab === 'wizard' && (
                    <DecisionPolicyWizard value={draft.rego} onChange={(rego) => patch({ rego })} skillNames={effectiveSkillNames} workflows={workflows} users={users} usersByRole={usersByRole} />
                  )}
                  {policyTab === 'rego' && (
                    <>
                      <div style={{ marginBottom: 6 }}>
                        <Button variant="secondary" size="sm" iconLeft={<Wand2 />}
                          onClick={() => { if (!draft.rego || !draft.rego.trim()) patch({ rego: REGO_SCAFFOLD }); }}
                          disabled={!!draft.rego && draft.rego.trim() !== ''}>
                          Insert agentlets.intent scaffold
                        </Button>
                      </div>
                      <textarea value={draft.rego} onChange={(e) => patch({ rego: e.target.value })} spellCheck={false} rows={14}
                        placeholder={'package agentlets.intent\ndefault allow = false\nallow { ... }'} style={codeAreaStyle} />
                    </>
                  )}
                </>
              ) : (
                <pre style={{ ...codeAreaStyle, margin: 0, whiteSpace: 'pre-wrap', overflow: 'auto', maxHeight: 320 }}>
                  {working.intentRego || '— no Rego authored —'}
                </pre>
              )}
              <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', marginTop: 6 }}>
                <FileCode size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Package <code style={{ fontFamily: 'ui-monospace, monospace' }}>agentlets.intent</code>, rule <code style={{ fontFamily: 'ui-monospace, monospace' }}>allow</code>.
                Consumes <code style={{ fontFamily: 'ui-monospace, monospace' }}>input.skill</code> + runtime context. Required before approval.
              </div>
            </div>
          </div>

          {/* 3 · dry-run (uses the live draft Rego) */}
          <DryRunPanel versionId={working.id} rego={editable ? draft.rego : working.intentRego} matchers={editable ? draft.matchers : working.allowedSkillMatchers} autonomy={draft.autonomy} />

          <IntentLayersExplainer active="template" />
        </>
      )}
    </div>
  );
}

/* ── lifecycle actions ───────────────────────────────────────────── */
function Lifecycle({ working, editable, canAuthor, dirty, saving, onSave, onSubmit, onApprove, onReject }) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  if (!working || !canAuthor) return null;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {editable && (
        <Button variant={dirty ? 'primary' : 'secondary'} size="sm" iconLeft={<Save />} loading={saving} disabled={!dirty && !saving} onClick={onSave}>
          Save draft
        </Button>
      )}
      {working.state === 'DRAFT' && (
        <Button variant="secondary" size="sm" iconLeft={<Send />} loading={saving} onClick={onSubmit}
          title="Submit for review (matchers + Rego required).">Submit for review</Button>
      )}
      {working.state === 'IN_REVIEW' && !rejecting && (
        <>
          <Button variant="primary" size="sm" iconLeft={<Check />} loading={saving} onClick={onApprove}
            title="Requires a different user than the author (separation of duties).">Approve</Button>
          <Button variant="secondary" size="sm" iconLeft={<X />} onClick={() => setRejecting(true)}>Reject</Button>
        </>
      )}
      {working.state === 'IN_REVIEW' && rejecting && (
        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Rejection reason" style={{ ...fieldStyle, width: 180 }} />
          <Button variant="danger" size="sm" loading={saving} disabled={!reason.trim()}
            onClick={async () => { const ok = await onReject(reason.trim()); if (ok) { setRejecting(false); setReason(''); } }}>Confirm</Button>
          <Button variant="secondary" size="sm" onClick={() => { setRejecting(false); setReason(''); }}>Cancel</Button>
        </span>
      )}
    </div>
  );
}

/* ── dry-run ─────────────────────────────────────────────────────── */
function DryRunPanel({ versionId, rego, matchers, autonomy }) {
  const [skill, setSkill] = useState('');
  const [dataClass, setDataClass] = useState('');
  const [auton, setAuton] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { setResult(null); }, [versionId]);

  const run = async () => {
    setRunning(true); setResult(null);
    const allowed = (Array.isArray(matchers) ? matchers : []).filter((m) => m.mode === 'skill-name' && m.value).map((m) => m.value);
    const sample = { skill: skill.trim(), allowed_skills: allowed, autonomy: auton || autonomy || '', data_class: dataClass.trim() || null };
    const res = await dryRunIntentRego(versionId, { rego, input: sample });
    setRunning(false);
    if (res?.ok === false) { setResult({ kind: 'ERROR', reason: res.error || 'Dry-run failed' }); return; }
    const d = res?.data ?? res;
    setResult({ kind: d?.kind || 'UNKNOWN', failure: d?.failure, reason: d?.reason || '' });
  };

  return (
    <div style={{ border: '1px solid var(--v2-border-default)', borderRadius: 6, padding: 12 }}>
      <SectionLabel><FlaskConical size={13} style={{ verticalAlign: 'middle', marginRight: 6 }} />3 · Dry-run this intent</SectionLabel>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 180px' }}><MiniLabel>skill</MiniLabel>
          <input value={skill} onChange={(e) => setSkill(e.target.value)} placeholder="mcp:submit_data" style={{ ...fieldStyle, fontFamily: 'ui-monospace, monospace' }} /></div>
        <div style={{ flex: '0 1 130px' }}><MiniLabel>data_class</MiniLabel>
          <input value={dataClass} onChange={(e) => setDataClass(e.target.value)} placeholder="PUBLIC" style={fieldStyle} /></div>
        <div style={{ flex: '0 1 160px' }}><MiniLabel>autonomy</MiniLabel>
          <select value={auton} onChange={(e) => setAuton(e.target.value)} style={fieldStyle}>
            <option value="">(none)</option><option value="SUPERVISED">SUPERVISED</option><option value="ASSISTED">ASSISTED</option>
            <option value="HUMAN_CONTROLLED">HUMAN_CONTROLLED</option><option value="AUTONOMOUS">AUTONOMOUS</option>
          </select></div>
        <Button variant="secondary" size="sm" iconLeft={running ? <Loader2 className="animate-spin" /> : <Play />} onClick={run} disabled={running || !rego || !rego.trim()}>
          {running ? 'Running…' : 'Simulate'}
        </Button>
      </div>
      {result && (() => {
        // POLICY_CONFLICT (authoring defect) arrives as kind=DENY; the shared view labels it
        // distinctly instead of a plain "DENY". Single source of truth: utils/regoVerdict.
        const view = regoVerdictView(result.kind, result.failure);
        const tone = REGO_TONE_COLORS[view.tone] || REGO_TONE_COLORS.warn;
        const hint = regoConflictHint(result.failure);
        return (
          <div style={{ marginTop: 10, display: 'flex', gap: 8, padding: '8px 10px', borderRadius: 6, fontSize: 12.5, background: tone.bg, color: tone.color }}>
            <strong>{view.label}</strong><span style={{ color: 'var(--v2-text-secondary)' }}>{result.reason}{hint && (result.reason ? ' — ' : '') + hint}</span>
          </div>
        );
      })()}
      {(!rego || !rego.trim()) && <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', marginTop: 8 }}>Author the Rego above first, then simulate.</div>}
    </div>
  );
}

/* ── matchers read view (non-DRAFT) ──────────────────────────────── */
function MatchersRead({ matchers }) {
  const arr = Array.isArray(matchers) ? matchers : [];
  if (arr.length === 0) return <div style={{ padding: 12, fontSize: 12.5, color: 'var(--v2-text-muted)', border: '1px solid var(--v2-border-default)', borderRadius: 6 }}>No matchers.</div>;
  return (
    <div style={{ border: '1px solid var(--v2-border-default)', borderRadius: 6, overflow: 'hidden' }}>
      {arr.map((m, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', fontSize: 12.5, borderTop: i ? '1px solid var(--v2-border-default)' : 'none' }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--v2-accent-cyan)', background: 'var(--v2-accent-cyan-soft)', padding: '1px 6px', borderRadius: 4 }}>
            {m.mode === 'skill-glob' ? 'glob' : (m.mode === 'explicit-skill' ? 'skill' : 'name')}
          </span>
          <code style={{ fontFamily: 'ui-monospace, monospace', flex: 1, minWidth: 0 }}>{m.value}</code>
        </div>
      ))}
    </div>
  );
}

/* ── primitives ──────────────────────────────────────────────────── */
const REGO_SCAFFOLD = `package agentlets.intent
# input.skill, input.allowed_skills[], input.intent, input.autonomy,
# input.risk_tier, input.data_class, input.args
default allow = false

allow {
    input.skill == input.allowed_skills[_]
    input.data_class != "restricted"
}
`;
const codeAreaStyle = {
  width: '100%', padding: '10px', fontSize: 12, lineHeight: 1.5, fontFamily: 'ui-monospace, SFMono-Regular, monospace',
  border: '1px solid var(--v2-border-strong)', borderRadius: 6, background: 'var(--v2-bg-muted)',
  color: 'var(--v2-text-primary)', outline: 'none', resize: 'vertical',
};
const fieldStyle = {
  width: '100%', height: 34, boxSizing: 'border-box', padding: '0 9px', fontSize: 12.5, lineHeight: '32px',
  border: '1px solid var(--v2-border-strong)', borderRadius: 6,
  background: 'var(--v2-bg-surface)', color: 'var(--v2-text-primary)', outline: 'none', fontFamily: 'inherit',
};
function VersionBadge({ version }) {
  const map = { DRAFT: { c: 'var(--v2-warning)', b: 'var(--v2-warning-soft)' }, IN_REVIEW: { c: 'var(--v2-accent-cyan)', b: 'var(--v2-accent-cyan-soft)' },
    APPROVED: { c: 'var(--v2-success)', b: 'var(--v2-success-soft)' }, DEPRECATED: { c: 'var(--v2-text-secondary)', b: 'var(--v2-bg-muted)' } };
  const s = map[version.state] || map.DRAFT;
  return <span style={{ fontSize: 11, fontWeight: 600, color: s.c, background: s.b, padding: '3px 9px', borderRadius: 999 }}>v{version.versionNumber} · {version.state}</span>;
}
function SectionLabel({ children }) {
  return <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--v2-text-secondary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{children}</div>;
}
function Faint({ children }) { return <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--v2-text-muted)' }}>{children}</span>; }
function MiniLabel({ children }) { return <label style={{ display: 'block', fontSize: 11, color: 'var(--v2-text-muted)', marginBottom: 3 }}>{children}</label>; }
function Inline({ children }) { return <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--v2-text-muted)', padding: 12 }}>{children}</div>; }
function Banner({ kind, children }) {
  const c = kind === 'error' ? 'var(--v2-danger)' : 'var(--v2-warning)';
  const b = kind === 'error' ? 'var(--v2-danger-soft)' : 'var(--v2-warning-soft)';
  return <div style={{ display: 'flex', gap: 10, padding: '10px 12px', color: c, background: b, border: `1px solid ${b}`, borderRadius: 6, fontSize: 12.5 }}><AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} /><span>{children}</span></div>;
}

export default IntentAuthoringConsole;
